/**
 * PASSO 3 — PESAGEM. Lote (repete o anterior), quantidade com − e +, peso e
 * observação. Feita para mão suja, sol e pressa: poucos campos, grandes.
 *
 * 3B · PORTEIRA: com duas porteiras ou mais (montadas na Negociação), aparecem
 * botões grandes com as porteiras — repetem a última, como o lote. É a única
 * decisão nova no curral: o gado não entra em ordem, e duas ou mais porteiras
 * ficam abertas ao mesmo tempo. Com uma porteira só (ou nenhuma), nada muda.
 *
 * 3E (test drive de 06/10/2026):
 *  - «Prévia» no lugar do «Ver romaneio parcial»: uma tabela simples na
 *    própria tela, por lote (categoria e preço) — cabeças, peso total e média
 *    —, e por porteira quando há apartação;
 *  - «Encerrar e ver romaneio» continua no fim da lista, por segurança;
 *  - quem não pode pesar (a Consulta) vê os totais, a prévia e a lista, sem
 *    nenhum botão de gravar.
 *
 * R1C (revisão de UX de 06/10/2026, pesagem inteira num celular de 375 px):
 *  - FORMA POR REGISTRO: «Balança · Caminhão (vazio e carregado) · Peso
 *    líquido», acima da quantidade, repetindo o último. O caminhão que aparece
 *    no meio de uma pesagem na balança não obriga mais a voltar duas vezes à
 *    Negociação. Registro antigo (sem a marca) lê a forma da ficha;
 *  - completar o caminhão é «Registrar carregado» e o aviso diz
 *    «#40 caminhão completo · 4.100 kg»;
 *  - depois de corrigir um registro antigo, lote, porteira, quantidade e
 *    forma VOLTAM aos de antes da correção, e o aviso diz para onde
 *    («Corrigido #24 · de volta a Bezerro → Fazenda Santa Rita»). Se o
 *    registro corrigido sumiu (a sincronização trocou a ficha), o app avisa
 *    e grava como registro novo — antes quebrava;
 *  - os totais numa linha só; «Registrar» fica preso acima da barra de baixo
 *    enquanto o formulário está na tela; a Prévia fecha sozinha ao registrar;
 *  - atalhos de quantidade 1 a 5; lote em botões grandes (até 4 lotes);
 *  - o ELEVADOR fica à esquerda, acima da barra de baixo, e só aparece
 *    quando o formulário de pesagem saiu da tela — nunca por cima do «+» da
 *    quantidade, da porteira ou do ✕ da lista;
 *  - tarja âmbar quando um lote está sem preço; ☀/🌙 para o tema claro.
 */
import { useEffect, useRef, useState, type RefObject } from 'react'
import { mudarFicha, usarEstado } from '../dados/armazem'
import { encerrar } from '../dados/acoes'
import { calcular, descontosDoLote, nomeDoLote, rendimentoPadrao } from '../dominio/contas'
import { nomeDaPorteira, porteiraDe, porteirasDa, temApartacao } from '../dominio/destinos'
import { agora, dataBR, diaDe, hora, kg, lerNumero, novoId, num, paraCampo } from '../dominio/numeros'
import { previaDaPesagem, textoDaMedia, type LinhaDaPrevia } from '../dominio/exportar'
import type { Ficha, Lote, ModoDePesagem, Registro } from '../dominio/tipos'
import { usarPode } from '../dados/sessao'
import { avisar, confirmar, Contador, Dica, Escolha, Janela, Tarja, type Ir } from '../ui'
import { EditorDeLote, loteDoRascunho, novoLoteRascunho, problemaDoLote } from './Negociacao'
import { pedirReabertura } from './Romaneio'
import { saidaAutomaticaDoEstoque, usarCategoriasDoEstoque } from './Catira'
import { BotaoDeTema } from './Ajustes'

/** R1C: os totais numa linha só (antes eram 4 quadros que empurravam o «Registrar» para baixo). */
export function Totais({ ficha, tema = false }: { ficha: Ficha; tema?: boolean }) {
  const r = calcular(ficha)
  return (
    <div className="placar-linha">
      <span><b>{r.cab}</b> cab</span>
      <span><b className="destaque">{kg(r.kg)}</b> kg</span>
      <span>média <b>{r.media === null ? '—' : num(r.media, 1)}</b></span>
      {r.temArroba && <span><b>{num(r.arrobas, 1)}</b> @</span>}
      {tema && <BotaoDeTema compacto />}
    </div>
  )
}

/** «Caminhão 13.900 − 9.800» — para a lista e para o romaneio. */
export function detalheDoRegistro(r: Registro): string {
  if (r.tara === null) return r.obs
  const cam = r.pendente ? `vazio ${kg(r.tara)} kg · aguardando carga` : `caminhão ${kg(r.bruto)} − ${kg(r.tara)}`
  return [cam, r.obs].filter(Boolean).join(' · ')
}

/**
 * R1C · A forma de um registro. Registro novo guarda a sua; o antigo se lê
 * pelos campos: tara preenchida é caminhão; senão vale a forma da ficha
 * (caminhão no balanção sem tara = peso líquido; curral = balança).
 */
export function modoDoRegistro(r: Pick<Registro, 'modo' | 'tara'>, f: Pick<Ficha, 'forma'>): ModoDePesagem {
  if (r.modo === 'balanca' || r.modo === 'caminhao' || r.modo === 'liquido') return r.modo
  if (r.tara !== null) return 'caminhao'
  return f.forma === 'caminhao' ? 'liquido' : 'balanca'
}

const MODOS: [ModoDePesagem, string][] = [['balanca', 'Balança'], ['caminhao', 'Caminhão (vazio e carregado)'], ['liquido', 'Peso líquido']]

/** O que a pesagem estava usando antes de abrir uma correção — para voltar a ele depois. */
interface Escolhas { loteId: string; destinoId: string | null; qtd: number; modo: ModoDePesagem }

export function Pesar({ ficha, ir }: { ficha: Ficha; ir: Ir }) {
  const { ajustes } = usarEstado()
  // R2D · Venda do meu estoque: o «+ Novo lote» também usa as categorias do estoque
  const doEstoque = usarCategoriasDoEstoque(ficha)
  const ultimo = ficha.registros[ficha.registros.length - 1]
  const [loteId, setLoteId] = useState(ultimo?.loteId ?? ficha.lotes[0]?.id ?? '')
  // a porteira repete a do último registro, como o lote
  const [destinoId, setDestinoId] = useState<string | null>(() => porteiraDe(ficha, ultimo?.destinoId))
  const [qtd, setQtd] = useState(ultimo?.qtd || 1)
  // R1C: a forma repete a do último registro; sem registro, a da ficha
  const [modo, setModo] = useState<ModoDePesagem>(() => (ultimo ? modoDoRegistro(ultimo, ficha) : ficha.forma === 'caminhao' ? 'caminhao' : 'balanca'))
  const [peso, setPeso] = useState('')
  const [tara, setTara] = useState('')
  const [bruto, setBruto] = useState('')
  const [obs, setObs] = useState('')
  const [editando, setEditando] = useState<string | null>(null)
  const [novoLote, setNovoLote] = useState<ReturnType<typeof novoLoteRascunho> | null>(null)
  const [previaAberta, setPreviaAberta] = useState(false)
  // R1C: recado que não pode sumir em 2 segundos (registro corrigido que tinha sumido)
  const [recado, setRecado] = useState('')
  const antesDaCorrecao = useRef<Escolhas | null>(null)
  // o número do registro em correção, guardado ao abrir: se ele sumir da ficha, o aviso ainda sabe qual era
  const nEmCorrecao = useRef<number | null>(null)
  const gravando = useRef(false)
  const campoPeso = useRef<HTMLInputElement>(null)
  const topoEntrada = useRef<HTMLDivElement>(null)
  const topoLeitura = useRef<HTMLDivElement>(null)
  const pode = usarPode()

  // ficha encerrada ou cancelada, ou quem não pode pesar (a Consulta): só leitura
  if (ficha.situacao !== 'aberta' || !pode('pesar')) {
    return (
      <div>
        <div ref={topoLeitura}><Totais ficha={ficha} tema /></div>
        <Previa ficha={ficha} />
        {ficha.situacao !== 'aberta' && pode('reabrir') && (
          <Tarja>
            Ficha {ficha.situacao}. Para acrescentar ou corrigir animais, reabra a ficha.{' '}
            {ficha.situacao === 'encerrada' && <button className="mini" onClick={() => { void pedirReabertura(ficha) }}>Reabrir</button>}
          </Tarja>
        )}
        <ListaDeRegistros ficha={ficha} />
        <Elevador alvo={topoLeitura} />
      </div>
    )
  }
  if (!ficha.lotes.length) {
    return (
      <div>
        <Tarja>Antes de pesar, cadastre ao menos um lote de negociação.</Tarja>
        <button className="botao grande" onClick={() => ir({ tela: 'ficha', id: ficha.id, aba: 'negociacao' })}>Ir para a negociação</button>
      </div>
    )
  }

  const lote = ficha.lotes.find((l) => l.id === loteId) ?? ficha.lotes[0]!
  // porteira que saiu da lista (ou ficha que ganhou porteiras agora): a primeira
  const porteira = porteiraDe(ficha, destinoId)
  const apartando = temApartacao(ficha)
  const vazioCarregado = modo === 'caminhao'
  const pesoOpcional = lote.base === 'cabeca'
  const pendentes = ficha.registros.filter((r) => r.pendente)
  const corrigindo = editando ? ficha.registros.find((r) => r.id === editando) : undefined
  const completandoCaminhao = !!corrigindo?.pendente
  const semPreco = ficha.lotes.filter((l) => l.preco === null || !(l.preco > 0))

  const liquidoCam = (() => {
    const t = lerNumero(tara), b = lerNumero(bruto)
    return t && b && b > t ? b - t : null
  })()
  const mediaDigitada = (() => {
    const k = vazioCarregado ? liquidoCam : lerNumero(peso)
    return k && qtd > 1 ? k / qtd : null
  })()

  function nomeDaPorteiraDe(id: string | null): string {
    return nomeDaPorteira(porteirasDa(ficha).find((p) => p.id === id) ?? null)
  }

  function limpar() {
    setPeso(''); setTara(''); setBruto(''); setObs(''); setEditando(null)
  }

  /** R1C: fim da correção — volta ao lote, porteira, quantidade e forma de antes. Devolve «Bezerro → Faz. X». */
  function voltarAsEscolhas(): string {
    const e = antesDaCorrecao.current
    antesDaCorrecao.current = null
    if (!e) return ''
    setLoteId(e.loteId); setDestinoId(e.destinoId); setQtd(e.qtd); setModo(e.modo)
    const l = ficha.lotes.find((x) => x.id === e.loteId)
    return `${l?.categoria ?? ''}${apartando ? ` → ${nomeDaPorteiraDe(e.destinoId)}` : ''}`
  }

  async function registrar() {
    if (gravando.current) return
    let dados: Pick<Registro, 'qtd' | 'kg' | 'tara' | 'bruto' | 'pendente' | 'obs' | 'modo'>
    if (vazioCarregado) {
      const t = lerNumero(tara), b = lerNumero(bruto)
      if (!(t && t > 0)) { window.alert('Digite o peso do caminhão vazio.'); return }
      if (b !== null && b <= t) { window.alert('O peso carregado tem que ser maior que o vazio.'); return }
      dados = { qtd, kg: b ? b - t : null, tara: t, bruto: b, pendente: !b, obs: obs.trim(), modo }
    } else {
      const k = lerNumero(peso)
      if (!pesoOpcional && !(k && k > 0)) { window.alert('Digite o peso.'); campoPeso.current?.focus(); return }
      dados = { qtd, kg: k && k > 0 ? k : null, tara: null, bruto: null, pendente: false, obs: obs.trim(), modo }
    }
    gravando.current = true
    try {
      if (dados.kg !== null) {
        const m = dados.kg / dados.qtd
        if ((m < 20 || m > 1400) && !await confirmar({
          titulo: 'Peso fora do normal',
          texto: `${dados.qtd > 1 ? 'Média de ' : ''}${num(m, 1)} kg por animal parece fora do normal.`,
          sim: 'Registrar mesmo assim', nao: 'Corrigir o peso',
        })) return
      }
      let n = 0
      let sumiu = false
      const nCorrigido = nEmCorrecao.current
      // ficha sem porteiras não ganha campo novo: o registro fica igual ao de sempre
      const marca = porteira ? { destinoId: porteira } : {}
      mudarFicha(ficha.id, (f) => {
        const r = editando ? f.registros.find((x) => x.id === editando) : undefined
        if (r) {
          Object.assign(r, dados, { loteId: lote.id }, marca)
          n = r.n
          return
        }
        // R1C: o registro em correção sumiu (a sincronização trocou a ficha) — grava como novo, sem quebrar
        if (editando) sumiu = true
        n = f.registros.reduce((mx, x) => Math.max(mx, x.n), 0) + 1
        f.registros.push({ id: novoId(), n, loteId: lote.id, em: agora(), ...dados, ...marca })
      })
      navigator.vibrate?.(40)
      const ondeFoi = apartando ? ` → ${nomeDaPorteiraDe(porteira)}` : ''
      const pesoTxt = dados.kg ? ` · ${kg(dados.kg)} kg` : ''
      if (editando && !sumiu) {
        const deVolta = voltarAsEscolhas()
        if (completandoCaminhao && !dados.pendente) avisar(`#${n} caminhão completo${pesoTxt}`)
        else avisar(`Corrigido #${n}${deVolta ? ` · de volta a ${deVolta}` : ''}`)
      } else {
        if (sumiu) {
          voltarAsEscolhas()
          setRecado(`O registro #${nCorrigido ?? ''} não está mais nesta ficha (a sincronização trouxe outra versão dela). O que você digitou foi gravado como registro novo, #${n}. Confira a lista.`)
        }
        avisar(dados.pendente ? `#${n} caminhão aguardando carga${ondeFoi}` : `Registrado #${n} · ${lote.categoria}${pesoTxt}${ondeFoi}`)
      }
      limpar()
      // R1C: a prévia fecha ao registrar — aberta, ela empurrava o formulário para baixo
      setPreviaAberta(false)
      campoPeso.current?.focus()
    } finally {
      gravando.current = false
    }
  }

  function corrigir(r: Registro) {
    // guarda o que estava escolhido para voltar a ele depois (R1C)
    if (!antesDaCorrecao.current) antesDaCorrecao.current = { loteId: lote.id, destinoId: porteira, qtd, modo }
    nEmCorrecao.current = r.n
    setEditando(r.id); setLoteId(r.loteId); setQtd(r.qtd); setObs(r.obs); setDestinoId(porteiraDe(ficha, r.destinoId))
    const m = modoDoRegistro(r, ficha)
    setModo(m)
    if (m === 'caminhao') { setTara(paraCampo(r.tara)); setBruto(paraCampo(r.bruto)); setPeso('') }
    else { setPeso(paraCampo(r.kg)); setTara(''); setBruto('') }
    topoEntrada.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  function cancelarCorrecao() {
    limpar()
    const deVolta = voltarAsEscolhas()
    if (deVolta) avisar(`Correção cancelada · de volta a ${deVolta}`)
  }

  async function excluirRegistro(r: Registro) {
    if (!await confirmar({ titulo: `Excluir o registro #${r.n}?`, texto: `${r.qtd} cab${r.kg ? ` · ${kg(r.kg)} kg` : ''}`, sim: 'Excluir', perigo: true })) return
    mudarFicha(ficha.id, (f) => { f.registros = f.registros.filter((x) => x.id !== r.id) })
    if (editando === r.id) cancelarCorrecao()
  }

  function adicionarLote() {
    if (!novoLote?.categoria) { window.alert('Escolha a categoria.'); return }
    const problema = problemaDoLote(novoLote)
    if (problema) { window.alert(problema); return }
    const l = loteDoRascunho(novoLote, rendimentoPadrao(novoLote.categoria, ajustes))
    mudarFicha(ficha.id, (f) => { f.lotes.push(l) })
    setLoteId(l.id); setNovoLote(null)
    avisar('Lote criado: ' + nomeDoLote(l))
  }

  async function encerrarFicha() {
    if (await pedirEncerramento(ficha)) ir({ tela: 'ficha', id: ficha.id, aba: 'romaneio' })
  }

  return (
    <div>
      <Totais ficha={ficha} tema />
      <Previa ficha={ficha} aberta={previaAberta} mudarAberta={setPreviaAberta} />
      {semPreco.length > 0 && (
        <Tarja onClick={() => ir({ tela: 'ficha', id: ficha.id, aba: 'negociacao' })}>
          {semPreco.length === 1 ? `Lote ${semPreco[0]!.categoria} está sem preço.` : `Lotes ${semPreco.map((l) => l.categoria).join(', ')} estão sem preço.`}
          {' '}Toque para pôr o preço na negociação.
        </Tarja>
      )}
      {pendentes.length > 0 && !completandoCaminhao && (
        <Tarja onClick={() => corrigir(pendentes[0]!)}>
          🚚 {pendentes.length === 1 ? 'Um caminhão aguarda' : `${pendentes.length} caminhões aguardam`} a pesagem carregado.
          Toque aqui ao voltar ao balanção.
        </Tarja>
      )}
      {recado && <Tarja tipo="erro" onClick={() => setRecado('')}>{recado} <u>Fechar</u></Tarja>}
      <div className={`cartao entrada${editando ? ' editando' : ''}`} ref={topoEntrada}>
        {editando && !corrigindo && <div className="tarja erro">O registro #{nEmCorrecao.current} não está mais nesta ficha (a sincronização trouxe outra versão dela). Ao salvar, vira registro novo.</div>}
        {corrigindo && <div className="tarja alerta">{completandoCaminhao ? `Completando o caminhão #${corrigindo.n}: digite o peso carregado` : `Corrigindo o registro #${corrigindo.n}`}</div>}
        <div className="linha fim">
          <div style={{ flex: 1, minWidth: 0 }}>
            {ficha.lotes.length > 4 && (
              <Escolha rotulo="Lote" valor={lote.id} mudar={setLoteId}
                opcoes={ficha.lotes.map((l): [string, string] => [l.id, nomeDoLote(l)])} />
            )}
            {ficha.lotes.length <= 4 && <label className="campo"><span>Lote</span></label>}
          </div>
          <button type="button" className="mini alto" onClick={() => setNovoLote(novoLoteRascunho(ajustes.base, rendimentoPadrao('', ajustes), descontosDoLote(ficha.lotes[ficha.lotes.length - 1] ?? {}, ficha)))}>+ Novo lote</button>
        </div>
        {ficha.lotes.length <= 4 && <BotoesDeLote ficha={ficha} valor={lote.id} mudar={setLoteId} />}
        {apartando && <BotoesDePorteira ficha={ficha} valor={porteira} mudar={setDestinoId} />}

        <label className="campo"><span>Como pesou este registro</span></label>
        <div className="segmento quebra">
          {MODOS.map(([v, t]) => (
            <button key={v} type="button" className={v === modo ? 'on' : ''} disabled={completandoCaminhao && v !== 'caminhao'}
              onClick={() => setModo(v)}>{t}</button>
          ))}
        </div>

        <label className="campo"><span>Quantidade de animais</span></label>
        <Contador valor={qtd} mudar={setQtd} />
        {/* R1C: atalhos de um toque — quase todo registro é de 1 a 5 cabeças */}
        <div className="atalhos-qtd" role="group" aria-label="Quantidade rápida">
          {[1, 2, 3, 4, 5].map((n) => (
            <button key={n} type="button" className={n === qtd ? 'on' : ''} aria-pressed={n === qtd} onClick={() => setQtd(n)}>{n}</button>
          ))}
        </div>

        {vazioCarregado ? (
          <>
            <div className="linha">
              <label className="campo grande-medio"><span>Vazio (kg)</span>
                <input inputMode="decimal" value={tara} onChange={(e) => setTara(e.target.value)} placeholder="0" /></label>
              <label className="campo grande-medio"><span>Carregado (kg)</span>
                <input inputMode="decimal" value={bruto} onChange={(e) => setBruto(e.target.value)} placeholder="depois"
                  autoFocus={completandoCaminhao} onKeyDown={(e) => { if (e.key === 'Enter') void registrar() }} /></label>
            </div>
            <div className="mini-dica">
              {liquidoCam ? <>Peso dos animais: <b>{kg(liquidoCam)} kg</b></> : 'Pesou só vazio? Registre: o caminhão fica aguardando, fora dos totais, até voltar carregado.'}
            </div>
          </>
        ) : (
          <label className="campo grande"><span>{modo === 'liquido' ? 'Peso líquido dos animais (kg)' : qtd > 1 ? `Peso dos ${qtd} animais (kg)` : 'Peso (kg)'}{pesoOpcional ? ' — opcional, lote por cabeça' : ''}</span>
            <input ref={campoPeso} inputMode="decimal" value={peso} onChange={(e) => setPeso(e.target.value)} placeholder="0"
              enterKeyHint="done" onKeyDown={(e) => { if (e.key === 'Enter') void registrar() }} /></label>
        )}
        {mediaDigitada !== null && <div className="mini-dica">Média: {num(mediaDigitada, 1)} kg por animal</div>}
        <label className="campo"><span>Observação</span>
          <input value={obs} onChange={(e) => setObs(e.target.value)} placeholder="opcional" /></label>
        {/* R1C: preso acima da barra de baixo enquanto o formulário está na tela (estilo.css) */}
        <button className="botao grande registrar-fixo" onClick={() => { void registrar() }}>
          {completandoCaminhao ? 'Registrar carregado' : editando ? 'Salvar correção' : 'Registrar'}</button>
        {editando && <button className="botao fantasma pequeno" onClick={cancelarCorrecao}>Cancelar {completandoCaminhao ? '' : 'correção'}</button>}
      </div>

      <ListaDeRegistros ficha={ficha} corrigir={corrigir} excluir={(r) => { void excluirRegistro(r) }} editando={editando} />

      <button className="botao" onClick={() => { void encerrarFicha() }}>Encerrar e ver o romaneio</button>
      <Elevador alvo={topoEntrada} />

      {novoLote && (
        <Janela titulo="Novo lote" fechar={() => setNovoLote(null)}>
          <Dica>Para animais com preço diferente do combinado — por exemplo, os que o vendedor insistiu e fez mais barato.
            Os descontos vêm do último lote; confira em <b>Descontos ▸</b>.</Dica>
          <EditorDeLote lote={novoLote} mudar={setNovoLote} categorias={doEstoque?.categorias ?? ajustes.categorias}
            rotuloDaCategoria={doEstoque?.rotulo} rendimentoDe={(c) => rendimentoPadrao(c, ajustes)} />
          <button className="botao" onClick={adicionarLote}>Criar lote e usar</button>
          <button className="botao fantasma" onClick={() => setNovoLote(null)}>Fechar</button>
        </Janela>
      )}
    </div>
  )
}

/**
 * Encerrar, com as conferências de sempre. Devolve true se encerrou. Usado
 * aqui e no Romaneio de ficha reaberta («Encerrar de novo»). R1C: a
 * confirmação é a janela do app.
 */
export async function pedirEncerramento(ficha: Ficha): Promise<boolean> {
  const r = calcular(ficha)
  if (!r.cab) { window.alert('Nenhum animal registrado ainda.'); return false }
  if (r.pendentes) { window.alert('Há caminhão aguardando a pesagem carregado. Complete ou exclua o registro antes de encerrar.'); return false }
  const ok = await confirmar({
    titulo: `Encerrar a ficha nº ${ficha.numero}?`,
    texto: `${r.cab} cabeças. Depois, para acrescentar ou corrigir animais, é só reabrir — e a reabertura fica anotada.`,
    sim: 'Encerrar e ver o romaneio',
  })
  if (!ok) return false
  encerrar(ficha.id)
  // R2D · «Venda do meu estoque»: sai sozinha do estoque, categoria por categoria (só o gestor)
  saidaAutomaticaDoEstoque(ficha.id)
  return true
}

/**
 * 3E · A «Prévia»: um botão que abre, na própria tela, a tabela por lote
 * (e por porteira). Sem cabeçalho nem formato de papel — é para decidir na
 * hora, não para entregar a ninguém. R1C: quem chama pode controlar se está
 * aberta (a Pesagem a fecha ao registrar).
 */
export function Previa({ ficha, sempreAberta = false, aberta: abertaDeFora, mudarAberta }: {
  ficha: Ficha; sempreAberta?: boolean; aberta?: boolean; mudarAberta?: (v: boolean) => void
}) {
  const [abertaAqui, setAbertaAqui] = useState(sempreAberta)
  const aberta = sempreAberta || (abertaDeFora ?? abertaAqui)
  const setAberta = mudarAberta ?? setAbertaAqui
  if (!ficha.registros.length) return null
  const p = previaDaPesagem(ficha)
  return (
    <div className="previa nao-imprimir">
      {!sempreAberta && (
        <button type="button" className="botao fantasma pequeno" aria-expanded={aberta} onClick={() => setAberta(!aberta)}>
          {aberta ? 'Fechar prévia ▴' : 'Prévia ▾'}</button>
      )}
      {aberta && (
        <>
          <TabelaDaPrevia titulo={p.porteiras.length ? 'Todos os animais, por lote' : 'Por lote'} lotes={p.lotes} total={p.total} />
          {p.porteiras.map((x) => <TabelaDaPrevia key={x.nome} titulo={`Porteira · ${x.nome}`} lotes={x.lotes} total={x.total} />)}
          {calcular(ficha).pendentes > 0 && <div className="mini-dica">Caminhão aguardando carga fica fora da prévia até pesar carregado.</div>}
        </>
      )}
    </div>
  )
}

/** R1C: a média é só dos pesados; com animal sem peso, diz quantos («191,1 (14 pesados)»). */
function mediaDaLinha(l: LinhaDaPrevia): string {
  return textoDaMedia(l.kg, l.cab, l.cabPesadas)?.replace(' kg', '') ?? '—'
}

function TabelaDaPrevia({ titulo, lotes, total }: { titulo: string; lotes: LinhaDaPrevia[]; total: LinhaDaPrevia }) {
  return (
    <div className="cartao tabela-previa">
      <div className="mini-dica" style={{ marginTop: 0, fontWeight: 700 }}>{titulo}</div>
      <table>
        <thead><tr><th>Lote</th><th className="n">Cab.</th><th className="n">Peso (kg)</th><th className="n">Média</th></tr></thead>
        <tbody>{lotes.map((l) => (
          <tr key={l.rotulo}><td>{l.rotulo}</td><td className="n">{l.cab}</td><td className="n">{l.kg ? kg(l.kg) : '—'}</td>
            <td className="n media">{mediaDaLinha(l)}</td></tr>
        ))}</tbody>
        <tfoot><tr><th>Total</th><th className="n">{total.cab}</th><th className="n">{total.kg ? kg(total.kg) : '—'}</th>
          <th className="n">{total.media === null ? '—' : num(total.media, 1)}</th></tr></tfoot>
      </table>
    </div>
  )
}

/**
 * O ELEVADOR (3E; R1C mudou de lugar): ↑ volta ao formulário de pesagem e ↓
 * vai ao fim da lista. Fica à ESQUERDA, acima da barra de baixo, e só
 * aparece quando o formulário saiu da tela — na revisão, ele ficava por cima
 * do «+» da quantidade, da porteira e do ✕ da lista, na coluna dos botões.
 */
function Elevador({ alvo }: { alvo: RefObject<HTMLDivElement | null> }) {
  const [fora, setFora] = useState(false)
  useEffect(() => {
    const el = alvo.current
    if (!el || typeof IntersectionObserver === 'undefined') return
    const obs = new IntersectionObserver(([e]) => setFora(!!e && !e.isIntersecting))
    obs.observe(el)
    return () => obs.disconnect()
  }, [alvo])
  if (!fora) return null
  return (
    <div className="elevador nao-imprimir">
      <button type="button" aria-label="Voltar ao formulário de pesagem" onClick={() => alvo.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}>↑</button>
      <button type="button" aria-label="Ir para o fim" onClick={() => window.scrollTo({ top: document.documentElement.scrollHeight, behavior: 'smooth' })}>↓</button>
    </div>
  )
}

/**
 * R1C · Até 4 lotes, botões grandes como os da porteira: a categoria grande,
 * o preço pequeno e quantas cabeças já passaram. Com mais de 4, a lista.
 */
function BotoesDeLote({ ficha, valor, mudar }: { ficha: Ficha; valor: string; mudar: (id: string) => void }) {
  const cabDe = (id: string) => ficha.registros.filter((r) => !r.pendente && r.loteId === id).reduce((s, r) => s + r.qtd, 0)
  const preco = (l: Lote) => {
    const nome = nomeDoLote(l)
    return nome === l.categoria ? 'sem preço' : nome.slice(l.categoria.length + 3)
  }
  return (
    <div className="segmento botoes-grandes" role="radiogroup" aria-label="Lote">
      {ficha.lotes.map((l) => (
        <button key={l.id} type="button" role="radio" aria-checked={l.id === valor} aria-label={`${l.categoria}, ${preco(l)}, ${cabDe(l.id)} cab`}
          className={l.id === valor ? 'on' : ''} onClick={() => mudar(l.id)}>
          <span className="grande">{l.categoria}</span>
          <small>{preco(l)} · {cabDe(l.id)} cab</small>
        </button>
      ))}
    </div>
  )
}

/**
 * As porteiras abertas, em botões grandes: um toque e a pesagem vai para lá.
 * Cada botão mostra quantas cabeças já passaram por aquela porteira.
 */
function BotoesDePorteira({ ficha, valor, mudar }: { ficha: Ficha; valor: string | null; mudar: (id: string) => void }) {
  const cabDe = (id: string) => ficha.registros.filter((r) => !r.pendente && porteiraDe(ficha, r.destinoId) === id).reduce((s, r) => s + r.qtd, 0)
  return (
    <>
      <label className="campo"><span>Porteira (para onde vai)</span></label>
      <div className="segmento botoes-grandes" role="radiogroup" aria-label="Porteira">
        {porteirasDa(ficha).map((p) => (
          <button key={p.id} type="button" role="radio" aria-checked={p.id === valor} aria-label={`${nomeDaPorteira(p)}, ${cabDe(p.id)} cab`}
            className={p.id === valor ? 'on' : ''} onClick={() => mudar(p.id)}>
            <span className="grande">{nomeDaPorteira(p)}</span><small>{cabDe(p.id)} cab</small>
          </button>
        ))}
      </div>
    </>
  )
}

function ListaDeRegistros({ ficha, corrigir, excluir, editando }: {
  ficha: Ficha; corrigir?: (r: Registro) => void; excluir?: (r: Registro) => void; editando?: string | null
}) {
  if (!ficha.registros.length) return <div className="cartao vazio">Nenhum animal registrado ainda.</div>
  const variosDias = new Set(ficha.registros.map((r) => diaDe(r.em))).size > 1
  const apartando = temApartacao(ficha)
  const porteiras = porteirasDa(ficha)
  let dia = ''
  return (
    <>
      <h2 className="secao">Registros ({ficha.registros.length})</h2>
      <div className="cartao">
        {ficha.registros.slice().reverse().map((r) => {
          const lote = ficha.lotes.find((l) => l.id === r.loteId)
          const d = diaDe(r.em)
          const cabecalho = variosDias && d !== dia ? <div className="dia">{dataBR(d)}</div> : null
          dia = d
          const det = detalheDoRegistro(r)
          return (
            <div key={r.id}>
              {cabecalho}
              <div className={`item${editando === r.id ? ' editando' : ''}`}>
                <div className="item-topo">
                  <span className="item-titulo">#{r.n} · {lote ? nomeDoLote(lote) : '?'}{r.qtd > 1 ? ` · ${r.qtd} cab` : ''}
                    {apartando && <> → {nomeDaPorteira(porteiras.find((p) => p.id === porteiraDe(ficha, r.destinoId)) ?? null)}</>}</span>
                  <span className="item-valor">{r.pendente ? <span className="etiqueta aberta">aguardando</span> : r.kg === null ? 'sem peso' : `${kg(r.kg)} kg`}</span>
                </div>
                <div className="item-baixo">
                  <span>{hora(r.em)}{det ? ' · ' + det : ''}{r.kg && r.qtd > 1 ? ` · média ${num(r.kg / r.qtd, 1)} kg` : ''}</span>
                  {corrigir && excluir && (
                    <span className="item-acoes">
                      <button className="mini acao" aria-label={r.pendente ? `Completar o caminhão #${r.n}` : `Corrigir o registro #${r.n}`}
                        onClick={() => corrigir(r)}>{r.pendente ? 'Completar' : '✎'}</button>
                      <button className="mini acao" aria-label={`Excluir o registro #${r.n}`} onClick={() => excluir(r)}>✕</button>
                    </span>
                  )}
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </>
  )
}
